"use client";

import * as React from "react";
import {
  DndContext,
  DragOverlay,
  pointerWithin,
  rectIntersection,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragStartEvent,
  type DragEndEvent,
  type DragOverEvent,
  type CollisionDetection,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  horizontalListSortingStrategy,
} from "@dnd-kit/sortable";
import { cn } from "../../../lib/utils";
import { kanbanBoardVariants, DEFAULT_MENU_ACTIONS } from "./constants";
import { StatusDot } from "../status-dot";
import { KanbanBoardProvider } from "./context";
import type {
  KanbanBoardProps,
  KanbanCardData,
  KanbanColumnData,
  KanbanMenuAction,
  KanbanActiveDrag,
  DropIndicatorState,
} from "./types";

const customCollisionDetection: CollisionDetection = (args) => {
  const rectCollisions = rectIntersection(args);

  const cardCollisions = rectCollisions.filter(
    (c) => !c.id.toString().endsWith(":drop-zone"),
  );
  const columnCollisions = rectCollisions.filter((c) =>
    c.id.toString().endsWith(":drop-zone"),
  );

  if (cardCollisions.length > 0) return cardCollisions;
  if (columnCollisions.length > 0) return columnCollisions;
  return pointerWithin(args);
};

const DROP_ANIMATION_CONFIG = {
  duration: 200,
  easing: "cubic-bezier(0.18, 0.67, 0.6, 1.22)",
};

const KanbanBoard = React.forwardRef<HTMLDivElement, KanbanBoardProps>(
  (
    {
      className,
      size = "default",
      columns,
      onCardMove,
      onColumnMove,
      onAddCard,
      onCardClick,
      onCardMenuAction,
      onWipLimitExceeded,
      renderCardOverlay,
      renderColumnOverlay,
      menuActions = DEFAULT_MENU_ACTIONS as unknown as KanbanMenuAction[],
      children,
      ...props
    },
    ref,
  ) => {
    const [activeDrag, setActiveDrag] = React.useState<KanbanActiveDrag | null>(
      null,
    );
    const [dropIndicator, setDropIndicator] =
      React.useState<DropIndicatorState>({ overCardId: null, position: null });

    const sensors = useSensors(
      useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
      useSensor(KeyboardSensor, {
        coordinateGetter: sortableKeyboardCoordinates,
      }),
    );

    // Pre-built Maps for O(1) lookups during drag events
    const { columnById, cardToColumn, cardById } = React.useMemo(() => {
      const colById = new Map<string, KanbanColumnData>();
      const cardToCol = new Map<string, KanbanColumnData>();
      const cardMap = new Map<string, KanbanCardData>();
      for (const col of columns) {
        colById.set(col.id, col);
        for (const card of col.cards) {
          cardToCol.set(card.id, col);
          cardMap.set(card.id, card);
        }
      }
      return {
        columnById: colById,
        cardToColumn: cardToCol,
        cardById: cardMap,
      };
    }, [columns]);

    const findColumnByCardId = React.useCallback(
      (cardId: string) => cardToColumn.get(cardId),
      [cardToColumn],
    );

    const findColumnById = React.useCallback(
      (id: string) => {
        const actualId = id.endsWith(":drop-zone") ? id.slice(0, -10) : id;
        return columnById.get(actualId);
      },
      [columnById],
    );

    const findCardById = React.useCallback(
      (cardId: string) => cardById.get(cardId),
      [cardById],
    );

    const handleDragStart = React.useCallback(
      (event: DragStartEvent) => {
        const { active } = event;
        const activeData = active.data.current;

        if (activeData?.type === "card") {
          const card = findCardById(String(active.id));
          if (card)
            setActiveDrag({ type: "card", id: String(active.id), data: card });
        } else if (activeData?.type === "column") {
          const column = findColumnById(String(active.id));
          if (column)
            setActiveDrag({
              type: "column",
              id: String(active.id),
              data: column,
            });
        }
      },
      [findCardById, findColumnById],
    );

    const handleDragOver = React.useCallback((event: DragOverEvent) => {
      const { active, over } = event;
      if (!over) {
        setDropIndicator({ overCardId: null, position: null });
        return;
      }

      const activeData = active.data.current;
      if (activeData?.type !== "card") {
        setDropIndicator({ overCardId: null, position: null });
        return;
      }

      const activeId = String(active.id);
      const overId = String(over.id);
      if (activeId === overId) {
        setDropIndicator({ overCardId: null, position: null });
        return;
      }

      const overData = over.data.current;

      if (overData?.type === "card") {
        const overRect = over.rect;
        const activeRect = active.rect.current.translated;
        const pointerY = activeRect
          ? activeRect.top + activeRect.height / 2
          : overRect.top;
        const cardCenterY = overRect.top + overRect.height / 2;
        setDropIndicator({
          overCardId: overId,
          position: pointerY < cardCenterY ? "before" : "after",
        });
      } else {
        setDropIndicator({ overCardId: null, position: null });
      }

      // Only update visual state during drag-over.
      // The actual card move fires in handleDragEnd.
    }, []);

    const handleDragEnd = React.useCallback(
      (event: DragEndEvent) => {
        const { active, over } = event;
        setActiveDrag(null);
        setDropIndicator({ overCardId: null, position: null });
        if (!over) return;

        const activeId = String(active.id);
        const overId = String(over.id);
        if (activeId === overId) return;

        const activeData = active.data.current;
        const overData = over.data.current;

        // Column reordering
        if (activeData?.type === "column") {
          const activeIdx = columns.findIndex((c) => c.id === activeId);
          const overIdx = columns.findIndex((c) => c.id === overId);
          if (activeIdx !== -1 && overIdx !== -1 && activeIdx !== overIdx) {
            onColumnMove?.(activeId, overIdx);
          }
          return;
        }

        // Card moves
        if (activeData?.type === "card") {
          const activeColumn = findColumnByCardId(activeId);
          const isOverColumn = overData?.type === "column";
          const overColumn = isOverColumn
            ? findColumnById(overId)
            : findColumnByCardId(overId) || findColumnById(overId);

          if (!activeColumn || !overColumn) return;

          if (
            activeColumn.id !== overColumn.id &&
            overColumn.wipLimit &&
            overColumn.wipLimit > 0 &&
            overColumn.cards.length >= overColumn.wipLimit
          ) {
            onWipLimitExceeded?.(overColumn.title);
            return;
          }

          const activeIndex = activeColumn.cards.findIndex(
            (c) => c.id === activeId,
          );
          if (activeIndex === -1) return;

          if (isOverColumn) {
            const newIndex = overColumn.cards.length;
            if (activeColumn.id !== overColumn.id || activeIndex !== newIndex) {
              onCardMove?.(activeId, activeColumn.id, overColumn.id, newIndex);
            }
            return;
          }

          if (activeColumn.id === overColumn.id) {
            const overIndex = overColumn.cards.findIndex(
              (c) => c.id === overId,
            );
            if (overIndex !== -1 && activeIndex !== overIndex) {
              onCardMove?.(activeId, activeColumn.id, overColumn.id, overIndex);
            }
          } else {
            const overIndex = overColumn.cards.findIndex(
              (c) => c.id === overId,
            );
            onCardMove?.(
              activeId,
              activeColumn.id,
              overColumn.id,
              overIndex >= 0 ? overIndex : overColumn.cards.length,
            );
          }
        }
      },
      [columns, findColumnByCardId, findColumnById, onCardMove, onColumnMove, onWipLimitExceeded],
    );

    const columnIds = React.useMemo(
      () => columns.map((col) => col.id),
      [columns],
    );

    const contextValue = React.useMemo(
      () => ({
        columns,
        activeDrag,
        dropIndicator,
        size,
        menuActions,
        onCardMove,
        onColumnMove,
        onAddCard,
        onCardClick,
        onCardMenuAction,
      }),
      [
        columns,
        activeDrag,
        dropIndicator,
        size,
        menuActions,
        onCardMove,
        onColumnMove,
        onAddCard,
        onCardClick,
        onCardMenuAction,
      ],
    );

    const defaultCardOverlay = (card: KanbanCardData) => (
      <div className="w-80 cursor-grabbing rounded-lg bg-elevated shadow-overlay px-2.5 py-3 opacity-80">
        <div className="text-sm font-medium line-clamp-2">{card.title}</div>
      </div>
    );

    const defaultColumnOverlay = (column: KanbanColumnData) => (
      /* Full height and fully opaque: the source column is hidden during the
         drag, so this is the only column on screen — a translucent stub would
         read as a gap. */
      <div className="h-full w-80 cursor-grabbing rounded-xl bg-panel p-2 shadow-overlay">
        <div className="flex h-control items-center gap-2 px-2">
          <StatusDot size="sm" color={column.color} label={column.title} />
          <span className="text-xs font-semibold text-text">{column.title}</span>
        </div>
        <div className="px-2 py-2 text-xs text-text-tertiary">
          {column.cards.length} cards
        </div>
      </div>
    );

    return (
      <KanbanBoardProvider value={contextValue}>
        <DndContext
          sensors={sensors}
          collisionDetection={customCollisionDetection}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={columnIds}
            strategy={horizontalListSortingStrategy}
          >
            <div
              ref={ref}
              className={cn(kanbanBoardVariants({ size }), className)}
              {...props}
            >
              {children}
            </div>
          </SortableContext>
          <DragOverlay dropAnimation={DROP_ANIMATION_CONFIG}>
            {activeDrag?.type === "card" &&
              (renderCardOverlay
                ? renderCardOverlay(activeDrag.data as KanbanCardData)
                : defaultCardOverlay(activeDrag.data as KanbanCardData))}
            {activeDrag?.type === "column" &&
              (renderColumnOverlay
                ? renderColumnOverlay(activeDrag.data as KanbanColumnData)
                : defaultColumnOverlay(activeDrag.data as KanbanColumnData))}
          </DragOverlay>
        </DndContext>
      </KanbanBoardProvider>
    );
  },
);

KanbanBoard.displayName = "KanbanBoard";

export { KanbanBoard };
